import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowRight,
  Boxes,
  CheckCircle2,
  CircleDollarSign,
  Clock3,
  DatabaseZap,
  Network,
  ShieldCheck,
  UsersRound,
  WalletCards
} from "lucide-react";
import { Link } from "react-router-dom";
import { StatusPill } from "@ocs/ui";
import { adminApi } from "../api";
import { amount, compactAmount, percentDelta, ratio } from "../format";

type Metrics = Record<string, string | number | null>;

type Dashboard = {
  treasury: { usdt_balance: string; block_number: string; snapshot_at: string } | null;
  nodes?: Metrics;
  market?: Metrics & {
    earnings?: Metrics;
    withdrawals?: Metrics;
    settlement?: Metrics;
    vaults?: Metrics;
    referrals?: Metrics;
  };
  withdrawals?: Metrics;
  fees?: { current_week_fee?: string };
  liquidity?: Array<Record<string, string | number>>;
  settlement: Metrics | null;
  updatedAt: string;
};

type Projection = {
  horizonDays: number;
  totalLiabilityUsdt: string;
  sageLiability: string;
  iageLiability: string;
  usdtCoverageRatio: string | null;
  sageCoverageRatio: string | null;
  iageCoverageRatio: string | null;
};

type MarketDashboard = {
  risk?: {
    vaultBalance?: { usdt: string; sage: string; iage: string };
    vaultDataComplete?: boolean;
    vaultDataFresh?: boolean;
    usdtSettlementEnabled?: boolean;
    payout?: {
      guard?: {
        passed: boolean;
        reasons: string[];
        minimumRequiredCoverageRatio: string;
        testedHorizonDays: number;
      };
      projections?: Projection[];
    };
    principalReserve?: {
      dataAvailable: boolean;
      usdtBalance: string;
      reservedUsdt: string;
      availableUsdt: string;
      usdtCoverageRatio: string | null;
      iageVaultBalance: string;
      reservedIagePrincipal: string;
      availableIageInventory: string;
    };
  };
  trend?: Array<Record<string, string | number>>;
  nodeSync?: Metrics | null;
};

const numeric = (value: string | number | null | undefined) => Number(value ?? 0);

const GUARD_REASONS: Record<string, string> = {
  PAYOUT_VAULT_SNAPSHOT_INCOMPLETE: "10 组 Vault 快照不完整",
  PAYOUT_VAULT_SNAPSHOT_STALE: "Vault 快照已过期",
  SAGE_COVERAGE_BELOW_MINIMUM: "SAGE 覆盖不足",
  IAGE_COVERAGE_BELOW_MINIMUM: "IAGE 覆盖不足",
  USDT_COVERAGE_BELOW_MINIMUM: "USDT 兑付覆盖不足"
};

type Tone = "success" | "warning" | "danger" | "neutral";

/** 一个红绿灯只回答一个每日必答问题，并给出下一步动作。 */
function Signal({ tone, question, answer, action, to }: {
  tone: Tone;
  question: string;
  answer: string;
  action?: string;
  to: string;
}) {
  return (
    <Link className={`ops-signal ops-signal--${tone}`} to={to}>
      <span className="ops-signal-dot" aria-hidden="true" />
      <div>
        <span>{question}</span>
        <strong>{answer}</strong>
        {action ? <p>{action}</p> : null}
      </div>
      <ArrowRight />
    </Link>
  );
}

/** 覆盖率进度条。缺口比比率本身有用——它直接就是多签要注资的金额。 */
function SolvencyBar({ label, unit, balance, requirement, coverage, hint }: {
  label: string;
  unit: string;
  balance: number;
  requirement: number | null;
  coverage: string | null;
  hint?: string;
}) {
  const shortfall = requirement == null ? 0 : Math.max(0, requirement - balance);
  const filled = requirement == null || requirement <= 0
    ? 100
    : Math.min(100, (balance / requirement) * 100);
  const tone: Tone = requirement == null ? "neutral" : shortfall > 0 ? "danger" : "success";
  return (
    <article className={`solvency-row solvency-row--${tone}`}>
      <div className="solvency-row-head">
        <span>{label}</span>
        <strong title={String(balance)}>{compactAmount(balance)} <small>{unit}</small></strong>
      </div>
      <div className="solvency-track"><i style={{ width: `${filled}%` }} /></div>
      <p>
        {requirement == null
          ? (hint ?? "等待快照")
          : shortfall > 0
            ? <><b>还差 {compactAmount(shortfall)} {unit}</b> 才能达标 · 覆盖 {ratio(coverage)}</>
            : <>达标 · 覆盖 {ratio(coverage)}</>}
      </p>
    </article>
  );
}

/** 内联 sparkline：不引图表库，避免为四条折线膨胀 bundle。 */
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return <span className="spark-empty">数据不足</span>;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const points = values.map((value, index) => {
    const x = (index / (values.length - 1)) * 100;
    const y = 26 - ((value - min) / span) * 24;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(" ");
  return (
    <svg className="spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
      <polyline points={points} fill="none" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function TrendCell({ label, values, unit }: { label: string; values: number[]; unit?: string }) {
  const latest = values.at(-1) ?? 0;
  const baseline = values.length > 1
    ? values.slice(0, -1).reduce((sum, value) => sum + value, 0) / (values.length - 1)
    : Number.NaN;
  const delta = percentDelta(latest, baseline);
  return (
    <article className="trend-cell">
      <span>{label}</span>
      <strong title={String(latest)}>{compactAmount(latest)}{unit ? <small> {unit}</small> : null}</strong>
      <Sparkline values={values} />
      <p className={delta == null ? "" : delta >= 0 ? "is-up" : "is-down"}>
        {delta == null ? "较 7 日均值 —" : `较 7 日均值 ${delta >= 0 ? "+" : ""}${delta.toFixed(1)}%`}
      </p>
    </article>
  );
}

function LedgerMetric({ label, value, unit }: {
  label: string;
  value: string | number | null | undefined;
  unit?: string;
}) {
  return <div><dt>{label}</dt><dd>{amount(value)}{unit ? <small> {unit}</small> : null}</dd></div>;
}

export function DashboardPage() {
  const query = useQuery({
    queryKey: ["admin-dashboard"],
    queryFn: () => adminApi<Dashboard>("/dashboard"),
    refetchInterval: 15000
  });
  // 储备闸门与本金储备来自 Market 看板，它包含链上读取，刷新频率放慢一档。
  const marketQuery = useQuery({
    queryKey: ["admin-market-dashboard"],
    queryFn: () => adminApi<MarketDashboard>("/market/dashboard"),
    refetchInterval: 60000
  });

  if (query.isLoading) return <div className="admin-skeleton"><i /><i /><i /></div>;
  if (query.isError || !query.data) return <div className="admin-error"><AlertTriangle /><h2>总览数据获取失败</h2><button onClick={() => query.refetch()}>重新请求</button></div>;

  const data = query.data;
  // Pages and the API are deployed independently. Keep the overview usable
  // during a rolling deployment even if one side still returns the previous
  // dashboard payload shape.
  const nodes = data.nodes ?? {};
  const market = data.market ?? {};
  const marketEarnings = market.earnings ?? {};
  const marketWithdrawals = market.withdrawals ?? {};
  const marketSettlement = market.settlement ?? {};
  const marketVaults = market.vaults ?? {};
  const marketReferrals = market.referrals ?? {};
  const withdrawals = data.withdrawals ?? {};
  const fees = data.fees ?? {};
  const direct = (data.liquidity ?? []).find((row) => Number(row.address_type) === 2);
  const nodePrincipal = numeric(nodes.active_principal);
  const marketPrincipal = numeric(market.active_principal);
  const marketFundedPrincipal = numeric(market.active_market_funded_principal);
  // A node identity also has a Market earning basis. Count its original
  // principal once, then add only capital actually funded through Market.
  const totalActivePrincipal = nodePrincipal + marketFundedPrincipal;
  const todayInflow = numeric(nodes.today_deposit_amount)
    + numeric(market.today_deposit_amount);
  const outstandingReward = numeric(nodes.outstanding_reward)
    + numeric(marketEarnings.outstanding_reward);
  const expectedSettlements = numeric(marketSettlement.expected_count);
  const completedSettlements = numeric(marketSettlement.completed_count);
  const failedSettlements = numeric(marketSettlement.failed_count);
  const settlementComplete = expectedSettlements === 0 || completedSettlements === expectedSettlements;
  const vaultCount = numeric(marketVaults.vault_count);
  const vaultReady = vaultCount === 10;
  const nodeWaiting = numeric(withdrawals.waiting_liquidity_count);
  const marketPending = numeric(marketWithdrawals.pending_count);
  const marketFailed = numeric(marketWithdrawals.failed_count);

  const risk = marketQuery.data?.risk;
  const guard = risk?.payout?.guard;
  const reserve = risk?.principalReserve;
  const vaultBalance = risk?.vaultBalance;
  const tested = risk?.payout?.projections
    ?.find((row) => row.horizonDays === guard?.testedHorizonDays);
  const requiredRatio = guard ? Number(guard.minimumRequiredCoverageRatio) : null;
  const requirementOf = (liability?: string) =>
    tested && requiredRatio != null && liability != null ? Number(liability) * requiredRatio : null;
  const usdtShortfall = Math.max(
    0,
    (requirementOf(tested?.totalLiabilityUsdt) ?? 0) - numeric(vaultBalance?.usdt)
  );

  const trend = marketQuery.data?.trend ?? [];
  const trendInflow = trend.map((row) => numeric(row.inflow));
  const trendEarning = trend.map(
    (row) => numeric(row.static_earning) + numeric(row.dynamic_earning)
  );

  // 运维事故与正常业务现象必须分开：接近三倍上限是业务结果，不是待处理故障。
  const incidentCount = failedSettlements + marketFailed + nodeWaiting
    + (guard && !guard.passed ? 1 : 0);
  const snapshotHealthy = risk ? Boolean(risk.vaultDataComplete && risk.vaultDataFresh) : null;

  return (
    <div className="dashboard-page overview-dashboard">
      <section className="overview-heading">
        <div>
          <p className="eyebrow">PLATFORM OVERVIEW</p>
          <h2>节点与 Market 双账本总览</h2>
          <p>本金、收益、兑付与结算按业务分账展示，平台风险统一汇总。</p>
        </div>
        <div className="overview-freshness">
          <StatusPill tone={incidentCount > 0 ? "warning" : "success"}>
            {incidentCount > 0 ? `${incidentCount} 项待处理` : "运行正常"}
          </StatusPill>
          <span>更新于 {new Date(data.updatedAt).toLocaleTimeString("zh-CN", { hour12: false })}</span>
        </div>
      </section>

      <section className="ops-signals" aria-label="今日运营状态">
        <Signal
          to="/market-operations"
          tone={!guard ? "neutral" : guard.passed ? "success" : "danger"}
          question="今天能发布收益率吗"
          answer={!guard ? "等待储备快照" : guard.passed ? "闸门通过，可发布" : "闸门拒绝"}
          action={!guard
            ? undefined
            : guard.passed
              ? `${guard.testedHorizonDays} 天压力口径覆盖达标`
              : guard.reasons.map((code) => GUARD_REASONS[code] ?? code).join(" · ")}
        />
        <Signal
          to="/market-operations"
          tone={failedSettlements > 0 ? "danger" : settlementComplete ? "success" : "warning"}
          question="昨日日结完成了吗"
          answer={`${completedSettlements} / ${expectedSettlements} 笔`}
          action={failedSettlements > 0
            ? `${failedSettlements} 笔失败，需人工重放`
            : settlementComplete ? "静态与动态均已入账" : "仍在处理中"}
        />
        <Signal
          to="/withdrawals"
          tone={nodeWaiting + marketFailed > 0 ? "danger" : marketPending > 0 ? "warning" : "success"}
          question="有没有卡住的队列"
          answer={`${nodeWaiting + marketPending + marketFailed} 笔`}
          action={`节点等流动性 ${nodeWaiting} · Market 待处理 ${marketPending} · 失败 ${marketFailed}`}
        />
        <Signal
          to="/market-operations"
          tone={!reserve?.dataAvailable
            ? "neutral"
            : numeric(reserve.availableUsdt) <= 0 ? "danger" : "success"}
          question="本金储备够不够"
          answer={reserve?.dataAvailable
            ? `可用 ${compactAmount(reserve.availableUsdt)} USDT`
            : "合约未配置"}
          action={reserve?.dataAvailable
            ? numeric(reserve.availableUsdt) <= 0
              ? "储备耗尽，新参与与加仓会全部失败"
              : `已为在投仓位预留 ${compactAmount(reserve.reservedUsdt)} USDT`
            : undefined}
        />
        <Signal
          to="/liquidity"
          tone={snapshotHealthy == null ? "neutral" : snapshotHealthy ? "success" : "warning"}
          question="链上快照新鲜吗"
          answer={snapshotHealthy == null
            ? "等待索引"
            : snapshotHealthy ? "完整且在有效期内" : "不完整或已过期"}
          action={`${vaultCount} / 10 组 Vault 已入账`}
        />
      </section>

      <section className="overview-capital-strip" aria-label="平台资金摘要">
        <article className="overview-capital-primary">
          <span>共享 Treasury 链上余额</span>
          <strong>{amount(data.treasury?.usdt_balance)} <small>USDT</small></strong>
          <p>区块 {data.treasury?.block_number ?? "等待快照"}</p>
        </article>
        <dl>
          <LedgerMetric label="全平台在管本金（不重复）" value={totalActivePrincipal} unit="USDT" />
          <LedgerMetric label="今日新增本金" value={todayInflow} unit="USDT" />
          <LedgerMetric label="待兑付收益" value={outstandingReward} unit="USDT" />
        </dl>
      </section>

      <section className="solvency-panel" aria-label="偿付能力">
        <header>
          <div>
            <p className="eyebrow">SOLVENCY</p>
            <h2>四条偿付线</h2>
          </div>
          <span>
            {guard
              ? `${guard.testedHorizonDays} 天压力口径 · 门槛 ${ratio(guard.minimumRequiredCoverageRatio)}`
              : "等待储备快照"}
          </span>
        </header>
        <div className="solvency-grid">
          <SolvencyBar
            label="本金储备池 USDT"
            unit="USDT"
            balance={numeric(reserve?.availableUsdt)}
            requirement={reserve?.dataAvailable ? 0 : null}
            coverage={reserve?.usdtCoverageRatio ?? null}
            hint="本金储备合约未配置"
          />
          <SolvencyBar
            label="10 组 USDT 兑付"
            unit="USDT"
            balance={numeric(vaultBalance?.usdt)}
            requirement={requirementOf(tested?.totalLiabilityUsdt)}
            coverage={tested?.usdtCoverageRatio ?? null}
          />
          <SolvencyBar
            label="10 个 Vault SAGE"
            unit="SAGE"
            balance={numeric(vaultBalance?.sage)}
            requirement={requirementOf(tested?.sageLiability)}
            coverage={tested?.sageCoverageRatio ?? null}
          />
          <SolvencyBar
            label="10 个 Vault IAGE"
            unit="IAGE"
            balance={numeric(vaultBalance?.iage)}
            requirement={requirementOf(tested?.iageLiability)}
            coverage={tested?.iageCoverageRatio ?? null}
          />
        </div>
        {usdtShortfall > 0 ? (
          <p className="solvency-action">
            多签需向 10 组 USDT 兑付合约合计注资约
            <b> {compactAmount(usdtShortfall)} USDT</b>，否则无法发布下一个日收益率。
          </p>
        ) : null}
      </section>

      <section className="trend-panel" aria-label="近 7 日趋势">
        <header>
          <div><p className="eyebrow">7 DAY TREND</p><h2>业务走势</h2></div>
          <Link to="/market-operations">查看完整趋势 <ArrowRight /></Link>
        </header>
        <div className="trend-grid">
          <TrendCell label="每日新增本金" values={trendInflow} unit="USDT" />
          <TrendCell label="每日日结收益" values={trendEarning} unit="USDT" />
        </div>
      </section>

      <section className="overview-ledgers" aria-label="节点与 Market 对比">
        <article className="overview-ledger overview-ledger--node">
          <header>
            <div className="overview-ledger-title"><Network /><div><span>NODE LEDGER</span><h2>节点业务</h2></div></div>
            <Link to="/nodes">查看节点明细 <ArrowRight /></Link>
          </header>
          <div className="overview-ledger-principal">
            <span>有效节点本金</span>
            <strong>{amount(nodePrincipal)} <small>USDT</small></strong>
            <p>{nodes.active_count ?? 0} 个有效节点 · 今日 +{amount(nodes.today_deposit_amount)}</p>
          </div>
          <dl className="overview-ledger-kpis">
            <LedgerMetric label="节点直推收益" value={nodes.direct_earning_total} unit="USDT" />
            <LedgerMetric label="节点分红收益" value={nodes.fee_dividend_total} unit="USDT" />
            <LedgerMetric label="待领取收益" value={nodes.outstanding_reward} unit="USDT" />
            <LedgerMetric label="累计收益净提现" value={withdrawals.total_confirmed_net} unit="USDT" />
          </dl>
          <div className="overview-ledger-status">
            <span>正常 {nodes.active_count ?? 0}</span>
            <span>违约 {nodes.breached_count ?? 0}</span>
            <span>到期退出 {nodes.redeemed_count ?? 0}</span>
            <span>三倍出局 {nodes.forced_exit_count ?? 0}</span>
          </div>
        </article>

        <article className="overview-ledger overview-ledger--market">
          <header>
            <div className="overview-ledger-title"><Boxes /><div><span>MARKET LEDGER</span><h2>Market 业务</h2></div></div>
            <Link to="/market-operations">进入 Market 监控 <ArrowRight /></Link>
          </header>
          <div className="overview-ledger-principal">
            <span>Market 收益计算本金</span>
            <strong>{amount(marketPrincipal)} <small>USDT</small></strong>
            <p>{market.active_count ?? 0} 个有效仓位 · {market.participant_count ?? 0} 个参与账户</p>
          </div>
          <dl className="overview-ledger-kpis">
            <LedgerMetric label="静态收益" value={marketEarnings.static_total} unit="USDT" />
            <LedgerMetric label="直推收益" value={marketEarnings.direct_total} unit="USDT" />
            <LedgerMetric label="团队 / 平超收益" value={marketEarnings.team_total} unit="USDT" />
            <LedgerMetric label="待兑付收益" value={marketEarnings.outstanding_reward} unit="USDT" />
          </dl>
          <div className="overview-ledger-status">
            <span>今日入金 {amount(market.today_deposit_amount)}</span>
            <span>Market 实际新增本金 {amount(marketFundedPrincipal)}</span>
            <span>节点双身份 {market.active_node_identity_count ?? 0}</span>
            <span>有效邀请 {marketReferrals.active_relation_count ?? 0}</span>
            <span>手续费分红 {amount(marketEarnings.fee_dividend_total)}</span>
            <span>接近三倍 {market.near_cap_count ?? 0}</span>
          </div>
        </article>
      </section>

      <section className="overview-operations">
        <header><div><p className="eyebrow">OPERATIONS PULSE</p><h2>兑付、结算与异常</h2></div><span>节点与 Market 风险口径分开计算</span></header>
        <div className="overview-operation-grid">
          <article>
            <div className="overview-operation-icon"><WalletCards /></div>
            <div><span>Market 兑付 Vault</span><strong>{amount(marketVaults.usdt_balance)} USDT</strong><p>SAGE {amount(marketVaults.sage_balance)} · IAGE {amount(marketVaults.iage_balance)}</p></div>
            <StatusPill tone={vaultReady ? "success" : "danger"}>{vaultCount} / 10 组</StatusPill>
          </article>
          <article>
            <div className="overview-operation-icon"><Clock3 /></div>
            <div><span>昨日 Market 日结</span><strong>{completedSettlements} / {expectedSettlements} 笔</strong><p>静态 {amount(marketSettlement.static_earning)} · 动态 {amount(marketSettlement.dynamic_earning)}</p></div>
            <StatusPill tone={failedSettlements > 0 ? "danger" : settlementComplete ? "success" : "warning"}>{failedSettlements > 0 ? `${failedSettlements} 笔失败` : settlementComplete ? "已完成" : "进行中"}</StatusPill>
          </article>
          <article>
            <div className="overview-operation-icon"><CircleDollarSign /></div>
            <div><span>节点手续费分红池</span><strong>{amount(fees.current_week_fee)} USDT</strong><p>本周可分红 30% · {amount(numeric(fees.current_week_fee) * .3)} USDT</p></div>
            <StatusPill tone={data.settlement ? "warning" : "success"}>{data.settlement ? "等待确认" : "等待周结"}</StatusPill>
          </article>
          <article>
            <div className="overview-operation-icon"><ShieldCheck /></div>
            <div><span>提现与流动性</span><strong>{nodeWaiting + marketPending} 笔待处理</strong><p>节点缺口 {amount(direct?.funding_gap as string | undefined)} · Market {amount(marketWithdrawals.pending_net_amount)}</p></div>
            <StatusPill tone={nodeWaiting + marketFailed > 0 ? "danger" : marketPending > 0 ? "warning" : "success"}>{nodeWaiting + marketPending > 0 ? "需要关注" : "无排队"}</StatusPill>
          </article>
        </div>
        <nav className="overview-shortcuts" aria-label="总览快捷入口">
          <Link to="/deposits"><ArrowDownToLine />查看全部入金</Link>
          <Link to="/liquidity"><DatabaseZap />检查合约流动性</Link>
          <Link to="/settlements"><CheckCircle2 />处理周结算</Link>
          <Link to="/treasury-flows"><UsersRound />查看 Treasury Flow</Link>
        </nav>
      </section>
    </div>
  );
}
